import * as React from "react"
import { type CategoryType } from "@digits-graphql/frontend/graphql-bearer"
import {
  CATEGORY_NUMBER_REGEX,
  NUMBERS_ONLY_PATTERN_REGEX,
} from "@digits-shared/components/UI/Elements/Form/Form"
import { ErrorText, Input } from "@digits-shared/DesignSystem/FormElements/Input"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import { type PrimitiveAtom, useAtom, useAtomValue } from "jotai"
import styled from "styled-components"
import {
  InputGroup,
  PanelLabel,
} from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Shared"
import { CATEGORY_NUMBER_PREFIX_MAP } from "src/frontend/components/OS/Applications/CategoryManager/Shared"

const InputWrapper = styled.div`
  position: relative;
  width: 200px;
`

const FixedNumber = styled(Input)`
  position: absolute;
  top: 0;
  left: 8px;
  height: 40px;
  z-index: 1;
  width: 10px;
  input[disabled] {
    border: 1px solid transparent;
    padding: 0;
    background-color: transparent;
    color: ${colors.secondary70};
  }
`

const AccountNumberInput = styled(Input)`
  &.strict > input {
    padding-left: 16px;
  }

  ${ErrorText} {
    ${fonts.scale.label};
  }
`

interface CategoryNumberInputProps {
  categoryNumberAtom: PrimitiveAtom<string>
  categoryNumbersAtom: PrimitiveAtom<Set<string>>
  originalCategoryNumber: string | undefined
  completeCategoryNumber?: string
  parentCategoryType?: CategoryType
  disabled?: boolean
  strict?: boolean
}

enum NumberInputError {
  INVALID_LENGTH = "Category number must contain 5 digits",
  NON_NUMERIC = "Category number is limited to digits",
  ALREADY_EXISTS = "This number is already taken",
}

export const CategoryNumberInput: React.FC<CategoryNumberInputProps> = ({
  categoryNumberAtom,
  categoryNumbersAtom,
  originalCategoryNumber,
  completeCategoryNumber,
  parentCategoryType,
  disabled,
  strict = false,
}) => {
  const [categoryNumber, setCategoryNumber] = useAtom(categoryNumberAtom)
  const categoryNumbers = useAtomValue(categoryNumbersAtom)
  const [errorText, setErrorText] = React.useState<NumberInputError | undefined>(undefined)

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCategoryNumber(e.target.value)
    },
    [setCategoryNumber]
  )

  React.useEffect(() => {
    const currentNumber = strict ? completeCategoryNumber : categoryNumber

    if (
      currentNumber !== originalCategoryNumber &&
      currentNumber &&
      categoryNumbers.has(currentNumber)
    ) {
      setErrorText(NumberInputError.ALREADY_EXISTS)
      return
    }

    if (strict && categoryNumber && completeCategoryNumber) {
      const numbersOnlyRegExp = new RegExp(NUMBERS_ONLY_PATTERN_REGEX)
      if (!numbersOnlyRegExp.test(completeCategoryNumber)) {
        setErrorText(NumberInputError.NON_NUMERIC)
        return
      }

      const categoryNumberRegExp = new RegExp(CATEGORY_NUMBER_REGEX)
      if (!categoryNumberRegExp.test(completeCategoryNumber)) {
        setErrorText(NumberInputError.INVALID_LENGTH)
        return
      }

      setErrorText(undefined)
      return
    }

    setErrorText(undefined)
  }, [categoryNumber, categoryNumbers, completeCategoryNumber, originalCategoryNumber, strict])

  return (
    <InputGroup>
      <PanelLabel>Category Number (Optional)</PanelLabel>

      <InputWrapper>
        {strict && (
          <FixedNumber
            type="text"
            value={parentCategoryType ? CATEGORY_NUMBER_PREFIX_MAP.get(parentCategoryType) : 0}
            disabled
          />
        )}

        <AccountNumberInput
          className={strict ? "strict" : ""}
          pattern={strict ? "[0-9]{4}" : undefined}
          placeholder={strict ? "0000" : "00000"}
          type="text"
          name="categoryNumber"
          value={categoryNumber}
          onChange={onChange}
          disabled={disabled}
          invalid={Boolean(errorText)}
          errorText={errorText}
        />
      </InputWrapper>
    </InputGroup>
  )
}
